import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/UI/Header';
import PersonalInfoSection from './components/PersonallinfoSection';
import SkillsMatrix from './components/SkillsMatrix';
import EducationSection from './components/EducationSection';
import AchievementsSection from './components/AchievementsSection';
import ResumeDownloadSection from './components/ResumeDownloadSection';
import PersonalInterestsSection from './components/PersonalInterestsSection';
import { AboutProfileData, PersonalInfo, Skill, Education, Achievement, ResumeFormat, PersonalInterest } from './types';
import { useContent } from '../../hooks/useContent';
import Footer from '../../components/UI/Footer';

import {
  defaultPersonalInfo,
  defaultSkills,
  defaultEducation,
  defaultAchievements,
  defaultResumeFormats,
  defaultInterests
} from './constants/defaults';

const AboutProfile = () => {
  // Fetch data from Supabase - REUSE existing sections where possible
  const { content: heroData, loading: loadingHero } = useContent<any>('hero', defaultPersonalInfo);
  const { content: contactData, loading: loadingContact } = useContent<any>('contact', { email: '', phone: '', location: '' });
  const { content: skills, loading: loadingSkills } = useContent<Skill[]>('skills', defaultSkills);
  const { content: education, loading: loadingEducation } = useContent<Education[]>('about_education', defaultEducation);
  const { content: achievements, loading: loadingAchievements } = useContent<Achievement[]>('about_achievements', defaultAchievements);
  const { content: resumeFormats, loading: loadingResume } = useContent<ResumeFormat[]>('about_resume', defaultResumeFormats);
  const { content: personalInterests, loading: loadingInterests } = useContent<PersonalInterest[]>('about_interests', defaultInterests);

  // Map hero + contact data to PersonalInfo format
  const personalInfo: PersonalInfo = {
    name: heroData?.name || defaultPersonalInfo.name,
    title: heroData?.title || defaultPersonalInfo.title,
    location: contactData?.location || defaultPersonalInfo.location,
    email: contactData?.email || defaultPersonalInfo.email,
    phone: contactData?.phone || defaultPersonalInfo.phone,
    yearsOfExperience: heroData?.yearsOfExperience || defaultPersonalInfo.yearsOfExperience,
    profileImage: heroData?.profileImage || defaultPersonalInfo.profileImage,
    profileImageAlt: heroData?.profileImageAlt || defaultPersonalInfo.profileImageAlt,
    bio: heroData?.description || heroData?.bio || defaultPersonalInfo.bio
  };

  const aboutData: AboutProfileData = {
    personalInfo,
    skills: Array.isArray(skills) ? skills : [],
    education: Array.isArray(education) ? education : [],
    achievements: Array.isArray(achievements) ? achievements : [],
    resumeFormats: Array.isArray(resumeFormats) ? resumeFormats : [],
    personalInterests: Array.isArray(personalInterests) ? personalInterests : []
  };

  const loading = loadingHero || loadingContact || loadingSkills || loadingEducation || loadingAchievements || loadingResume || loadingInterests;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="neubrutal-card bg-white p-10 flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#0C2C39]/10 border-t-[#0C2C39] rounded-full animate-spin mb-4"></div>
          <p className="font-black uppercase tracking-widest text-xs text-[#0C2C39]/60">Initializing Directory...</p>
        </div>
      </div>
    );
  }

  // Ensure we have valid arrays
  const safeEducation = Array.isArray(aboutData.education) ? aboutData.education : [];
  const safeAchievements = Array.isArray(aboutData.achievements) ? aboutData.achievements : [];
  const safeInterests = Array.isArray(aboutData.personalInterests) ? aboutData.personalInterests : [];

  return (
    <>
      <Helmet>
        <title>About {personalInfo.name} - {personalInfo.title} | Portfolio</title>
        <meta name="description" content={`Learn about ${personalInfo.name}'s professional background, skills, education, and achievements.`} />
        <meta name="keywords" content={`${personalInfo.name}, ${personalInfo.title}, Developer, Portfolio, About, Skills, Education`} />
        <meta property="og:title" content={`About ${personalInfo.name} - ${personalInfo.title}`} />
        <meta property="og:description" content={personalInfo.bio} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />

        <main className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-32 space-y-16 sm:space-y-24">
          <PersonalInfoSection personalInfo={aboutData.personalInfo} />
          <SkillsMatrix skills={aboutData.skills} />
          {safeEducation.length > 0 && <EducationSection education={safeEducation} />}
          {safeAchievements.length > 0 && <AchievementsSection achievements={safeAchievements} />}
          <ResumeDownloadSection resumeFormats={aboutData.resumeFormats} />
          {safeInterests.length > 0 && <PersonalInterestsSection personalInterests={safeInterests} />}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutProfile;